import { useEffect } from "react";
import { SITE } from "@/lib/site";

/**
 * Sets page <title>, meta description, canonical, and Open Graph tags per route.
 * Optionally injects a JSON-LD structured-data block via the `jsonLd` param
 * (accepts a single object or an array of objects). The JSON-LD script is
 * scoped by a `data-seo` attribute so it doesn't collide with other scripts
 * and gets cleaned up when the page unmounts.
 */
export default function useSEO({ title, description, path = "", image, type = "website", jsonLd = null }) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} | ${SITE.name}`
      : `${SITE.name} · Architectural Vinyl, Signage & Installation`;
    const desc =
      description ||
      "Custom signs, architectural wraps, window film, wall graphics, and professional vinyl installation across Toronto and the GTA. 3M Certified. Free quotes & mockups.";
    const url = `${SITE.url}${path}`;
    const ogImage = image || `${SITE.url}/og-default.jpg`;

    document.title = fullTitle;

    setMeta("name", "description", desc);
    setMeta("name", "robots", "index, follow, max-image-preview:large");
    setLink("canonical", url);

    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", SITE.name);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:locale", "en_CA");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", ogImage);

    setJsonLd(jsonLd);
    return () => {
      // Clean up the JSON-LD block so it doesn't linger when the user navigates away.
      const el = document.head.querySelector('script[data-seo="jsonld"]');
      if (el) el.remove();
    };
  }, [title, description, path, image, type, jsonLd]);
}

function setJsonLd(payload) {
  const existing = document.head.querySelector('script[data-seo="jsonld"]');
  if (existing) existing.remove();
  if (!payload) return;
  const el = document.createElement("script");
  el.setAttribute("type", "application/ld+json");
  el.setAttribute("data-seo", "jsonld");
  el.textContent = JSON.stringify(payload);
  document.head.appendChild(el);
}

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
